const sizeVariantToWrap: { [key: string]: string } = {
  sm: "@media (width >= 40rem)",
  md: "@media (width >= 48rem)",
  lg: "@media (width >= 64rem)",
  xl: "@media (width >= 80rem)",
  "2xl": "@media (width >= 96rem)",
};

const addOrEditCSSRule = (
  documentToApplyTo: Document,
  selector: string,
  rules: string,
  id: string,
) => {
  const styleEl = documentToApplyTo.createElement("style");
  styleEl.id = id;

  const textContent = `
  ${selector} {${rules}}
  `;
  styleEl.textContent = textContent;

  documentToApplyTo.head.appendChild(styleEl);
};

export const addClassCssRule = (
  documentToApplyTo: Document,
  className: string,
  rules: string,
  id: string,
  variant?: string | null,
) => {
  let selector = className
    .replace(/\[/g, "\\[")
    .replace(/\]/g, "\\]")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)")
    .replace(/\./g, "\\.")
    .replace(/\%/g, "\\%")
    .replace(/\!/g, "\\!")
    .replace(/\//g, "\\/")
    .replace(/#/g, "\\#");

  let ruleToSet = rules;

  if (variant && variant !== "default") {
    selector = `${variant}\\:${selector}`;

    if (variant === "hover") {
      selector = `${selector}:hover`;
    } else if (sizeVariantToWrap[variant]) {
      ruleToSet = `${sizeVariantToWrap[variant]} { ${rules} }`;
    }
  }

  selector = `.${selector}`;
  try {
    addOrEditCSSRule(documentToApplyTo, selector, ruleToSet, id);
  } catch (e) {
    console.log("Error adding class CSS rule", e);
  }
};

export const removeClassCssRule = (documentToApplyTo: Document, id: string) => {
  const styleEl = documentToApplyTo.getElementById(id);
  if (styleEl) {
    styleEl.remove();
  }
};

export const getElementsForCodebaseId = (
  iframeDocument: Document,
  codebaseId: string,
): Element[] => {
  return Array.from(
    iframeDocument.querySelectorAll(`[data-tempoelementid="${codebaseId}"]`),
  );
};

// Comprehensive function to fix all types of URLs in the document
export const changeHtmlToUseBaseUrl = (doc: Document, baseUrl: string) => {
  if (!baseUrl) return;

  const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;

  // Helper function to check if URL is relative
  const isRelativeUrl = (url) => {
    if (!url || typeof url !== "string") return false;

    // Skip protocol-relative URLs
    if (url.startsWith("//")) return false;

    // This is a relative URL that should be resolved
    return url?.startsWith("/") && !url?.startsWith("//");
  };

  // Helper function to resolve relative URL
  const resolveUrl = (url) => {
    try {
      return new URL(url, normalizedBaseUrl).href;
    } catch (e) {
      console.warn(`Failed to resolve URL: ${url}`, e);
      return url; // Return original if resolution fails
    }
  };

  // 1. Fix CSS URLs in style elements
  const styleElements = doc.querySelectorAll("style");
  styleElements.forEach((styleEl) => {
    if (styleEl.textContent) {
      styleEl.textContent = styleEl.textContent.replace(
        /url\(\s*['"]?([^'")\s]+)['"]?\s*\)/g,
        (match, url) => {
          if (isRelativeUrl(url)) {
            return `url("${resolveUrl(url)}")`;
          }
          return match;
        },
      );
    }
  });

  // 2. Fix inline style background-image and other CSS properties
  const elementsWithStyles = doc.querySelectorAll("[style]");
  elementsWithStyles.forEach((el) => {
    const style = el.getAttribute("style");
    if (style) {
      const updatedStyle = style.replace(
        /url\(\s*['"]?([^'")\s]+)['"]?\s*\)/g,
        (match, url) => {
          if (isRelativeUrl(url)) {
            return `url("${resolveUrl(url)}")`;
          }
          return match;
        },
      );
      el.setAttribute("style", updatedStyle);
    }
  });

  // 3. Fix image sources
  const images = doc.querySelectorAll("img[src]");
  images.forEach((img) => {
    const src = img.getAttribute("src");
    if (isRelativeUrl(src)) {
      img.setAttribute("src", resolveUrl(src));
    }
  });

  // 4. Fix image srcset attributes
  const imagesWithSrcset = doc.querySelectorAll("img[srcset]");
  imagesWithSrcset.forEach((img) => {
    const srcset = img.getAttribute("srcset");
    if (srcset) {
      const updatedSrcset = srcset.replace(
        /(\S+)(\s+[\d.]+[wx])?/g,
        (match, url, descriptor) => {
          if (isRelativeUrl(url)) {
            return resolveUrl(url) + (descriptor || "");
          }
          return match;
        },
      );
      img.setAttribute("srcset", updatedSrcset);
    }
  });

  // 5. Fix video sources
  const videos = doc.querySelectorAll("video[src]");
  videos.forEach((video) => {
    const src = video.getAttribute("src");
    if (isRelativeUrl(src)) {
      video.setAttribute("src", resolveUrl(src));
    }
  });

  // 6. Fix video source elements
  const videoSources = doc.querySelectorAll("video source[src]");
  videoSources.forEach((source) => {
    const src = source.getAttribute("src");
    if (isRelativeUrl(src)) {
      source.setAttribute("src", resolveUrl(src));
    }
  });

  // 7. Fix audio sources
  const audios = doc.querySelectorAll("audio[src]");
  audios.forEach((audio) => {
    const src = audio.getAttribute("src");
    if (isRelativeUrl(src)) {
      audio.setAttribute("src", resolveUrl(src));
    }
  });

  // 8. Fix audio source elements
  const audioSources = doc.querySelectorAll("audio source[src]");
  audioSources.forEach((source) => {
    const src = source.getAttribute("src");
    if (isRelativeUrl(src)) {
      source.setAttribute("src", resolveUrl(src));
    }
  });

  // 9. Fix iframe sources
  const iframes = doc.querySelectorAll("iframe[src]");
  iframes.forEach((iframe) => {
    const src = iframe.getAttribute("src");
    if (isRelativeUrl(src)) {
      iframe.setAttribute("src", resolveUrl(src));
    }
  });

  // 10. Fix embed sources
  const embeds = doc.querySelectorAll("embed[src]");
  embeds.forEach((embed) => {
    const src = embed.getAttribute("src");
    if (isRelativeUrl(src)) {
      embed.setAttribute("src", resolveUrl(src));
    }
  });

  // 11. Fix object data attributes
  const objects = doc.querySelectorAll("object[data]");
  objects.forEach((obj) => {
    const data = obj.getAttribute("data");
    if (isRelativeUrl(data)) {
      obj.setAttribute("data", resolveUrl(data));
    }
  });

  // 12. Fix anchor hrefs (links)
  const links = doc.querySelectorAll("a[href]");
  links.forEach((link) => {
    const href = link.getAttribute("href");
    if (isRelativeUrl(href)) {
      link.setAttribute("href", resolveUrl(href));
    }
  });

  // 13. Fix area hrefs (image maps)
  const areas = doc.querySelectorAll("area[href]");
  areas.forEach((area) => {
    const href = area.getAttribute("href");
    if (isRelativeUrl(href)) {
      area.setAttribute("href", resolveUrl(href));
    }
  });

  // 14. Fix form actions
  const forms = doc.querySelectorAll("form[action]");
  forms.forEach((form) => {
    const action = form.getAttribute("action");
    if (isRelativeUrl(action)) {
      form.setAttribute("action", resolveUrl(action));
    }
  });

  // 15. Fix external stylesheet links
  const stylesheetLinks = doc.querySelectorAll('link[rel="stylesheet"][href]');
  stylesheetLinks.forEach((link) => {
    const href = link.getAttribute("href");
    if (isRelativeUrl(href)) {
      link.setAttribute("href", resolveUrl(href));
    }
  });

  // 16. Fix other link elements (icons, preload, etc.)
  const otherLinks = doc.querySelectorAll('link[href]:not([rel="stylesheet"])');
  otherLinks.forEach((link) => {
    const href = link.getAttribute("href");
    if (isRelativeUrl(href)) {
      link.setAttribute("href", resolveUrl(href));
    }
  });

  // 17. Fix meta refresh URLs
  const metaRefresh = doc.querySelectorAll(
    'meta[http-equiv="refresh"][content]',
  );
  metaRefresh.forEach((meta) => {
    const content = meta.getAttribute("content");
    if (content && content.includes("url=")) {
      const updatedContent = content.replace(/url=([^;]+)/i, (match, url) => {
        const trimmedUrl = url.trim();
        if (isRelativeUrl(trimmedUrl)) {
          return `url=${resolveUrl(trimmedUrl)}`;
        }
        return match;
      });
      meta.setAttribute("content", updatedContent);
    }
  });

  // 18. Fix SVG image hrefs and xlink:href
  const svgImages = doc.querySelectorAll("image[href], image[xlink\\:href]");
  svgImages.forEach((img) => {
    const href = img.getAttribute("href") || img.getAttribute("xlink:href");
    if (isRelativeUrl(href)) {
      const resolvedUrl = resolveUrl(href);
      if (img.hasAttribute("href")) {
        img.setAttribute("href", resolvedUrl);
      }
      if (img.hasAttribute("xlink:href")) {
        img.setAttribute("xlink:href", resolvedUrl);
      }
    }
  });

  // 19. Fix SVG use elements
  const svgUses = doc.querySelectorAll("use[href], use[xlink\\:href]");
  svgUses.forEach((use) => {
    const href = use.getAttribute("href") || use.getAttribute("xlink:href");
    if (isRelativeUrl(href)) {
      const resolvedUrl = resolveUrl(href);
      if (use.hasAttribute("href")) {
        use.setAttribute("href", resolvedUrl);
      }
      if (use.hasAttribute("xlink:href")) {
        use.setAttribute("xlink:href", resolvedUrl);
      }
    }
  });

  // 20. Fix track sources (for video/audio subtitles)
  const tracks = doc.querySelectorAll("track[src]");
  tracks.forEach((track) => {
    const src = track.getAttribute("src");
    if (isRelativeUrl(src)) {
      track.setAttribute("src", resolveUrl(src));
    }
  });

  // 21. Fix picture source elements
  const pictureSources = doc.querySelectorAll("picture source[srcset]");
  pictureSources.forEach((source) => {
    const srcset = source.getAttribute("srcset");
    if (srcset) {
      const updatedSrcset = srcset.replace(
        /(\S+)(\s+[\d.]+[wx])?/g,
        (match, url, descriptor) => {
          if (isRelativeUrl(url)) {
            return resolveUrl(url) + (descriptor || "");
          }
          return match;
        },
      );
      source.setAttribute("srcset", updatedSrcset);
    }
  });

  // 22. Fix elements with data-src (lazy loading)
  const lazyElements = doc.querySelectorAll("[data-src]");
  lazyElements.forEach((el) => {
    const dataSrc = el.getAttribute("data-src");
    if (isRelativeUrl(dataSrc)) {
      el.setAttribute("data-src", resolveUrl(dataSrc));
    }
  });

  // 23. Fix elements with data-srcset (lazy loading)
  const lazySrcsetElements = doc.querySelectorAll("[data-srcset]");
  lazySrcsetElements.forEach((el) => {
    const dataSrcset = el.getAttribute("data-srcset");
    if (dataSrcset) {
      const updatedSrcset = dataSrcset.replace(
        /(\S+)(\s+[\d.]+[wx])?/g,
        (match, url, descriptor) => {
          if (isRelativeUrl(url)) {
            return resolveUrl(url) + (descriptor || "");
          }
          return match;
        },
      );
      el.setAttribute("data-srcset", updatedSrcset);
    }
  });
};

export const FORCE_FULL_WIDTH_HEIGHT_CSS = `
  .tempo-force-full-width-height {
    width: 100vw !important;
    height: 100vh !important;
  }
`;

export const FORCE_FULL_WIDTH_HEIGHT_CLASS_ID = "tempo-force-full-width-height";
